import api from "../../../core/api";
import type { Location } from "../components/locations/locationTypes";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.customersApiBaseUrl };
};

export interface CreateLocationViewModel {
  name: string;
  parentLocationId?: string;
  description?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  countryId?: number;
  zipCode?: string;
  // MAP-A-FE1/BE — additive nullable coordinates. Until MAP-A-BE deploys these
  // two members are silently dropped by ASP.NET Core model binding (harmless).
  latitude?: number;
  longitude?: number;
}

export interface ChangeLocationViewModel {
  name: string;
  parentLocationId?: string;
  description?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  countryId?: number;
  zipCode?: string;
  // MAP-A-FE1 — edit DTO already accepts these server-side (BE1, live .cc).
  latitude?: number;
  longitude?: number;
}

// LOC-DELETE (SETTINGS_V2_LOC_DELETE_SPEC §7.1) — FROZEN 409 contract for the
// permanent hard-delete endpoint. `referencedBy` uses the fixed reason
// vocabulary: children | default | machines | serviceRequests | workOrders |
// maintenanceTeams | userAssignments. `unverified` names fan-out services that
// failed (⇒ code === "LOC_DELETE_UNVERIFIED", retryable).
export interface LocationDeleteBlocked {
  code: "LOC_DELETE_BLOCKED" | "LOC_DELETE_UNVERIFIED";
  message?: string;
  referencedBy: string[];
  counts: Record<string, number>;
  unverified: string[];
}

export type LocationDeleteResult =
  | { deleted: true }
  | { deleted: false; blocked: LocationDeleteBlocked };

class CustomerLocationsApi {
  getLocations(): Promise<Location[]> {
    return api
      .get<Location[]>(`/v1/locations`, getRequestConfig())
      .then((x) => x.data);
  }

  getLocation(id: string): Promise<Location> {
    return api
      .get<Location>(`/v1/locations/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  createLocation(data: CreateLocationViewModel): Promise<string> {
    return api
      .post<string>(`/v1/locations`, data, getRequestConfig())
      .then((x) => x.data);
  }

  updateLocation(id: string, data: ChangeLocationViewModel): Promise<void> {
    return api
      .put<void>(`/v1/locations/${id}`, data, getRequestConfig())
      .then(() => {});
  }

  disableLocation(id: string): Promise<void> {
    return api
      .delete(`/v1/locations/${id}`, getRequestConfig())
      .then(() => {});
  }

  enableLocation(id: string): Promise<void> {
    return api
      .post(`/v1/locations/${id}/enable`, null, getRequestConfig())
      .then(() => {});
  }

  setDefaultLocation(id: string): Promise<void> {
    return api
      .post(`/v1/locations/${id}/set-default`, null, getRequestConfig())
      .then(() => {});
  }

  // LOC-DELETE (§7.1) — the global core/api.ts interceptor collapses every
  // non-bot rejection into { message, code }, which would STRIP the structured
  // 409 body (referencedBy/counts/unverified). We do NOT edit core/api.ts;
  // instead we treat 409 as a RESOLVED response via per-request validateStatus,
  // so the full body survives to the page. 401/403/404/5xx still reject through
  // the interceptor unchanged (403 keeps the global access-denied toast).
  deleteLocationPermanently(id: string): Promise<LocationDeleteResult> {
    return api
      .delete(`/v1/locations/${id}/permanent`, {
        ...getRequestConfig(),
        validateStatus: (s) => (s >= 200 && s < 300) || s === 409,
      })
      .then((x) => {
        if (x.status !== 409) return { deleted: true };
        // Defensive: a 409 body is JSON, but tolerate a string body (wrong
        // content-type) so the structured reasons still reach the page.
        let data: unknown = x.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            data = {};
          }
        }
        return { deleted: false, blocked: (data ?? {}) as LocationDeleteBlocked };
      });
  }
}

export default new CustomerLocationsApi();
